import { Component } from "react";
import { Warning, ArrowClockwise } from "@phosphor-icons/react";
import { logger } from "@/lib/logger";

/**
 * App-level error boundary. Catches render-time crashes and shows a friendly
 * fallback (with the failing route preserved) instead of a blank white screen.
 */
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    logger.error("Render error captured by ErrorBoundary:", error, info);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div data-testid="error-boundary" className="flex min-h-screen flex-col items-center justify-center bg-background px-6 text-center">
        <div className="flex h-14 w-14 items-center justify-center bg-destructive/10 text-destructive">
          <Warning className="h-8 w-8" weight="fill" />
        </div>
        <h1 className="mt-6 font-heading text-2xl font-extrabold tracking-tight">Something went wrong</h1>
        <p className="mt-2 max-w-md text-sm text-muted-foreground">
          The page hit an unexpected error. Reloading usually fixes it. If it persists, contact your administrator.
        </p>
        <button
          data-testid="error-boundary-reload"
          onClick={this.handleReload}
          className="mt-6 inline-flex items-center gap-2 bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
        >
          <ArrowClockwise className="h-4 w-4" weight="bold" /> Reload page
        </button>
      </div>
    );
  }
}
